import { API, graphqlOperation } from "aws-amplify";
import {
  addDashboardQuery,
  addEmailBodyToDashboard,
  addUser,
  addWorkspaceToDashboard,
  addWorkspaceUserMembership,
  assignContentToDashboard,
  createDashboard,
  createDocSendIngestion,
  createGroup,
  createIntegration,
  createOauth2Creds,
  createUnassignedContent,
  createWorkspace,
  deleteDashboard,
  deleteDashboardQuery,
  deleteUnassignedContent,
  deleteUser,
  deleteWorkspace,
  deleteWorkspaceUserMembership,
  refreshDashboardQuery,
  refreshWorkspaceScore,
  removeWorkspaceFromDashboard,
  resendUserInvite,
  runFileSync,
  sendRequestIntroEmail,
  sendShareWorkspaceEmail,
  updateCurrentUserWorkspaceUserMembership,
  updateDashboard,
  updateFile,
  updateGroupSettings,
  updateIntegration,
  updateInvestmentThesis,
  updateUser,
  updateWorkspace
} from "../graphql/mutations";
import {
  getAllGroupSettings,
  getCurrentUserWorkspaceUserMemberships,
  getDashboard,
  getDashboardQueries,
  getFiles,
  getGroupActiveSubscription,
  getGroupDashboards,
  getGroupIntegrations,
  getGroupSettings,
  getPublicDashboard,
  getPublicDashboardQueries,
  getPublicFilesForDashboard,
  getPublicWorkspace,
  getUnassignedContentItem,
  getUnassignedContentItems,
  getUsage,
  getUser,
  getUserByReferralCode,
  getUsers,
  getWorkspaceDashboards,
  getWorkspaceScore,
  getWorkspaceUserMemberships,
  listIntegrationSubfolders
} from "../graphql/queries";
import queryClient from "../services/queryClient";
import { GroupSettings, PaginatedGroupSettings, User } from "../types/common";
import {
  AssignUnassignedContentInput,
  CompanyFile,
  Dashboard,
  DashboardQuery,
  DashboardQuerySource,
  DocSendIngestion,
  EmailBody,
  InvestmentThesis,
  MarketMetric,
  UnassignedContent,
  Workspace,
  WorkspaceUserMembership
} from "../types/files";
import {
  IntegrationState,
  ListIntegrationSubfoldersRequest,
  OAuth2CredsRequest,
} from "../types/integrations";
import instanceHelper from "../utils/InstanceHelper";
import storage from "../utils/storage";


// HELPERS ...........................................................

const handleCachedErrorData = (error: any) => <T>(previous: T) => {
  const next: T = structuredClone(previous);

  if (storage.getItem('config.logger-api'))
    console.log(['Error in query:', error, previous]);
  if (Array.isArray(next)) {
    if (typeof next[0] === 'object')
      return [{_error: error, ...next[0]}, ...next];
    else if (typeof next[0] === 'string')
      return next.map(item => `${item}!`);

    return [null];
  } else if (typeof next === 'object') {
    return {_error: error, ...next};
  }

  return null;
};

export const prefetchGetFunc = async (queryFns: Array<any|((...params: any[]) => Promise<any>)>) => {
  try {
    await Promise.all(queryFns);
  } catch (error) {
    if (storage.getItem('config.logger-api'))
      console.error('Error while prefetchGet:', error);
  }
};

export const invalidateQueries = async (queryKey: string[], refresh?: boolean) => {
   if (refresh) {
    await queryClient.refetchQueries({ queryKey, exact: true });
  } else {
    await Promise.resolve(queryClient.removeQueries(queryKey, { exact: true }));
  }
};

export const setNullOrder = (newDashboard: any) => {
  let newDashboardDetails = structuredClone(newDashboard);
  let length = -1;

  if (newDashboardDetails.documents) {
    newDashboardDetails.documents.forEach((document: any) => {
      if (document.order > length) {
        length = document.order;
      }
    });
  }
  if (newDashboardDetails.screenshots) {
    newDashboardDetails.screenshots.forEach((document: any) => {
      if (document.order > length) {
        length = document.order;
      }
    });
  }
  if (newDashboardDetails.selections) {
    newDashboardDetails.selections.forEach((document: any) => {
      if (document.order > length) {
        length = document.order;
      }
    });
  }

  if (newDashboardDetails.documents) {
    newDashboardDetails.documents = newDashboardDetails.documents.map(
      (document: any) => {
        if (!document.order && document.order !== 0) {
          document.order = length + 1;
          length += 1;
        }
        return document;
      }
    );
  }

  if (newDashboardDetails.screenshots) {
    newDashboardDetails.screenshots = newDashboardDetails.screenshots.map(
      (document: any) => {
        if (!document.order && document.order !== 0) {
          document.order = length + 1;
          length += 1;
        }
        return document;
      }
    );
  }

  if (newDashboardDetails.selections) {
    newDashboardDetails.selections = newDashboardDetails.selections.map(
      (document: any) => {
        if (!document.order && document.order !== 0) {
          document.order = length + 1;
          length += 1;
        }
        return document;
      }
    );
  }
  return newDashboardDetails;
};


// QUERIES ...........................................................

export const getFilesFunc = async (group: string, dashboardId?: string) => {
  const queryKey: string[] = ['getFiles', group, dashboardId || ''];
  const queryFn = async () => {
    const result: any = await API.graphql(
      graphqlOperation(getFiles, { group, dashboardId })
    );
    return (result?.data?.getFiles?.items ?? []) as CompanyFile[];
  };
  let queryData: CompanyFile[] = [];

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getFiles:', error);
  }

  return queryData;
};

export const getUserFunc = async ({ email, username }: {email?: string, username?: string}) => {
  const queryKey: string[] = ['getUser', email || username || ''];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getUser,
      variables: { email, username },
      authMode: 'AWS_IAM',
    });
    return result.data?.getUser as any;
  };
  let queryData: any | undefined = undefined;

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getUser:', error);
  }

  return queryData;
};

export const getUserByReferralCodeFunc = async ({ referralCode }: {referralCode?: string}) => {
  const queryKey: string[] = ['getUserByReferralCode', referralCode || ''];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getUserByReferralCode,
      variables: { referralCode },
      authMode: 'AWS_IAM',
    });
    return result.data?.getUserByReferralCode as any;
  };
  let queryData: any | undefined = undefined;

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getUser:', error);
  }

  return queryData;
};

export const getAllUsersFunc = async (group: string) => {
  const queryKey: string[] = ['getAllUsers', group];
  const queryFn = async () => {
    const result: any = await API.graphql(
      graphqlOperation(getUsers, { group })
    );
    return result?.data?.getUsers?.items ?? [];
  };
  let queryData: any[] = [];

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getAllUsers:', error);
  }

  return queryData;
};

export const getGroupActiveSubscriptionFunc = async ({ group } : { group: string }) => {
  const queryKey: string[] = ['getGroupActiveSubscription', group];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getGroupActiveSubscription,
      variables: { group },
    });
    return result?.data?.getGroupActiveSubscription as any;
  };
  let queryData: any | undefined = undefined;

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getGroupActiveSubscription:', error);
  }

  return queryData;
};

export const getUsageFunc = async ({ group } : { group: string }) => {
  const queryKey: string[] = ['getUsage', group];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getUsage,
      variables: { group },
    });
    return result.data.getUsage as any;
  };
  let queryData: any | undefined = undefined;

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getUsage:', error);
  }

  return queryData;
};

export const getIntegrationsFunc = async (group: string) => {
  const queryKey: string[] = ['getIntegrations', group];
  const queryFn = async () => {
    const result: any = await API.graphql(
      graphqlOperation(getGroupIntegrations, { group })
    );
    return result?.data?.getGroupIntegrations?.items;
  };
  let queryData: any[] = [];

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getIntegrations:', error);
  }

  return queryData;
};

export const listIntegrationSubfoldersFunc = async (variables: ListIntegrationSubfoldersRequest) => {
  const queryKey: string[] = ['listIntegrationSubfolders', variables?.group || ''];
  const queryFn = async () => {
    const result: any = await API.graphql(
      graphqlOperation(listIntegrationSubfolders, variables)
    );
    return result?.data?.listIntegrationSubfolders ?? [];
  };
  let queryData: any[] = [];

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while listIntegrationSubfolders:', error);
  }

  return queryData;
};

export const getAllGroupSettingsFunc = async ({ nextToken, limit } : { nextToken?: string, limit?: number }) => {
  const queryKey: string[] = ['getAllGroupSettings', nextToken ?? ''];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getAllGroupSettings,
      variables: { nextToken, limit },
    });
    return result.data.getAllGroupSettings as PaginatedGroupSettings;
  };
  let queryData: PaginatedGroupSettings | undefined = undefined;

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getAllGroupSettings:', error);
  }

  return queryData;
};

export const getGroupSettingsFunc = async (variables: { group?: string }) => {
  const queryKey: string[] = ['getGroupSettings', variables.group ?? ''];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getGroupSettings,
      variables
    });
    return result.data.getGroupSettings as GroupSettings;
  }
  let queryData: GroupSettings | undefined = undefined;

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getGroupSettings:', error);
  }

  return queryData;
};

export const getWorkspaceUserMembershipsFunc = async ({ workspaceId } : { workspaceId: string }) => {
  const queryKey: string[] = ['getWorkspaceUserMemberships', workspaceId];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getWorkspaceUserMemberships,
      variables: {
        workspaceId,
      },
    });
    return result.data.getWorkspaceUserMemberships as any;
  }
  let queryData: {items: WorkspaceUserMembership[]} | undefined = undefined;

  try {
    queryData = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 1000 * 5 });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getWorkspaceUserMemberships:', error);
  }

  return queryData;
};

export const getCurrentUserWorkspaceUserMembershipsFunc = async () => {
  const queryKey: string[] = ['getCurrentUserWorkspaceUserMemberships'];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getCurrentUserWorkspaceUserMemberships,
    });
    return result.data.getCurrentUserWorkspaceUserMemberships as any[];
  };
  let queryData: any[] = [];

  try {
    queryData = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 1000 * 2.5 });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getCurrentUserWorkspaceUserMemberships:', error);
  }

  return queryData;
};

export const getWorkspaceDashboardsFunc = async ({ workspaceId, fetchMode } : { workspaceId: string, fetchMode?: boolean }) => {
  const queryKey: string[] = ['getWorkspaceDashboards', workspaceId];
  const queryFn = async () => {
    const result: any = await API.graphql(
      graphqlOperation(getWorkspaceDashboards, { workspaceId })
    );

    let items = result?.data?.getWorkspaceDashboards?.items ?? [];
    let nextToken = result?.data?.getWorkspaceDashboards?.nextToken;

    while (nextToken) {
      const nextResult: any = await API.graphql(
        graphqlOperation(getWorkspaceDashboards, { workspaceId, nextToken })
      );

      nextToken = nextResult?.data?.getWorkspaceDashboards?.nextToken;
      items.push(...nextResult?.data?.getWorkspaceDashboards?.items ?? []);
    }

    return items as Dashboard[];
  };
  let queryData: Dashboard[] = [];

  try {
    queryData = fetchMode ? await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 })
      : await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getWorkspaceDashboards:', error);
  }

  return queryData;
};

export const getWorkspaceScoreFunc = async ({ workspaceId, group } : { workspaceId: string, group: string }) => {
  const queryKey: string[] = ['getWorkspaceScore', workspaceId, group];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getWorkspaceScore,
      variables: {
        workspaceId,
        group,
      },
    });
    return result.data.getWorkspaceScore as any;
  };
  let queryData: any | undefined = undefined;

  try {
    queryData = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 1000 * 5 });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getWorkspaceScore:', error);
  }

  return queryData;
};

export const getPublicWorkspaceFunc = async ({ id, group } : { id: string, group?: string }) => {
  const queryKey: string[] = ['getPublicWorkspace', group ?? '', id];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getPublicWorkspace,
      variables: { id, group },
      authMode: 'AWS_IAM',
    });
    return result?.data?.getPublicWorkspace as { workspace: Workspace, dashboards: Dashboard[] };
  };
  let queryData: { workspace: Workspace, dashboards: Dashboard[] } | undefined = undefined;

  try {
    queryData = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getPublicWorkspace:', error);
  }

  return queryData;
};

export const getGroupDashboardsFunc = async (group: string) => {
  const queryKey: string[] = ['getGroupDashboards', group];
  const fetchPage = async (nextToken?: string) => {
    const result: any = await API.graphql(
      graphqlOperation(getGroupDashboards, { group, nextToken })
    );
    const newToken: string | undefined = result?.data?.getGroupDashboards?.nextToken || undefined;
    const items: Dashboard[] = result?.data?.getGroupDashboards?.items ?? [];

    return { items, newToken };
  };
  const queryFn = async () => {
    const allItems: Dashboard[] = [];
    let nextToken: string | undefined = undefined;

    do {
      const { items, newToken } : {items: Dashboard[], newToken: string | undefined} = await fetchPage(nextToken);
      allItems.push(...items.filter((dq: any) => !dq.deletedAt));
      nextToken = newToken;
    } while (nextToken);

    return allItems;
  };
  let queryData: Dashboard[] = [];

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getGroupDashboards:', error);
  }

  return queryData;
};

export const getGroupDashboardQueriesFunc = async (group: string) => {
  const queryKey: string[] = ['getGroupDashboardQueries', group];
  const fetchPage = async (nextToken?: string) => {
    const result: any = await API.graphql(
      graphqlOperation(getDashboardQueries, { group, nextToken })
    );
    const newToken: string | undefined = result?.data?.getDashboardQueries?.nextToken || undefined;
    const items: DashboardQuery[] = result?.data?.getDashboardQueries?.items ?? [];

    return { items, newToken };
  };
  const queryFn = async () => {
    const allItems: DashboardQuery[] = [];
    let nextToken: string | undefined = undefined;

    do {
      const { items, newToken } : {items: DashboardQuery[], newToken: string | undefined} = await fetchPage(nextToken);
      allItems.push(...items.filter((dq: any) => !dq.deletedAt));
      nextToken = newToken;
    } while (nextToken);

    return allItems;
  };
  let queryData: DashboardQuery[] = [];

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getGroupDashboardQueries:', error);
  }

  return queryData;
};

export const getDashboardById = async (id: string) => {
  const queryKey: string[] = ['getDashboardById', id];
  const queryFn = async () => {
    const result: any = await API.graphql(
      graphqlOperation(getDashboard, { id })
    );
    return (result?.data?.getDashboard ?? null) as Dashboard;
  };
  let queryData: Dashboard | undefined = undefined;

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getDashboardById:', error);
  }

  return queryData;
};

export const getDashboardQueriesFunc = async (dashboardId: string, title?: string) => {
  const queryKey: string[] = ['getDashboardQueries', dashboardId, title || ''];
  const queryFn = async () => {
    const result: any = await API.graphql(
      graphqlOperation(getDashboardQueries, { dashboardId, title })
    );
    return (result?.data?.getDashboardQueries?.items ?? []) as DashboardQuery[];
  };
  let queryData: DashboardQuery[] = [];

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getDashboardQueries:', error);
  }

  return queryData;
};

export const getPublicDashboardById = async (id: string) => {
  const queryKey: string[] = ['getPublicDashboardById', id];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getPublicDashboard,
      variables: { id },
      authMode: 'AWS_IAM',
    });
    return (result?.data?.getPublicDashboard ?? null) as Dashboard;
  };
  let queryData: Dashboard | undefined = undefined;

  try {
    queryData = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getPublicDashboardById:', error);
  }

  return queryData;
};

export const getPublicDashboardQueriesFunc = async (dashboardId: string, title?: string) => {
  const queryKey: string[] = ['getPublicDashboardQueries', dashboardId, title || ''];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getPublicDashboardQueries,
      variables: { id: dashboardId, title },
      authMode: 'AWS_IAM',
    });
    return (result?.data?.getPublicDashboardQueries ?? []) as DashboardQuery[];
  };
  let queryData: DashboardQuery[] = [];

  try {
    queryData = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getPublicDashboardQueries:', error);
  }

  return queryData;
};

export const getPublicFilesForDashboardFunc = async (id: string) => {
  const queryKey: string[] = ['getPublicFilesForDashboard', id];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getPublicFilesForDashboard,
      variables: { dashboardId: id },
      authMode: 'AWS_IAM',
    });
    return result.data.getPublicFilesForDashboard as CompanyFile[];
  };
  let queryData: CompanyFile[] = [];

  try {
    queryData = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getPublicFilesForDashboard:', error);
  }

  return queryData;
};

export const getUnassignedContentItemsFunc = async ({ group } : { group: string }) => {
  const queryKey: string[] = ['getUnassignedContentItems', group];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getUnassignedContentItems,
      variables: { group },
    });
    return result.data.getUnassignedContentItems as UnassignedContent[];
  };
  let queryData: UnassignedContent[] = [];

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getUnassignedContentItems:', error);
  }

  return queryData;
};

export const getUnassignedContentItemFunc = async (id: string) => {
  const queryKey: string[] = ['getUnassignedContentItem', id];
  const queryFn = async () => {
    const result: any = await API.graphql({
      query: getUnassignedContentItem,
      variables: { id },
    });
    return result.data.getUnassignedContentItem as UnassignedContent;
  };
  let queryData: UnassignedContent | undefined = undefined;

  try {
    queryData = await queryClient.ensureQueryData({ queryKey, queryFn });
  } catch (error) {
    queryClient.setQueryData(queryKey, handleCachedErrorData(error));
    if (storage.getItem('config.logger-api'))
      console.log('Error while getUnassignedContentItem:', error);
  }

  return queryData;
};


// MUTATIONS ..........................................................

export const createOauth2CredsFunc = async (variables: OAuth2CredsRequest) => {
  try {
    const result: any = await API.graphql({
      query: createOauth2Creds,
      variables
    });

    return result.data.createOauth2Creds as any;
  } catch (error) {
    console.log('Error while createOauth2Creds:', error);
    throw error;
  }

  // return undefined;
};

export const createIntegrationFunc = async (data: IntegrationState) => {
  try {
    const result: any = await API.graphql({
      query: createIntegration,
      variables: data,
    });

    return result.data.createIntegration as any;
  } catch (error) {
    console.log('Error while createIntegration:', error);
    // throw error;
  }

  return undefined;
};

export const updateIntegrationFunc = async (data: IntegrationState) => {
  try {
    const result: any = await API.graphql({
      query: updateIntegration,
      variables: data,
    });

    return result.data.updateIntegration as any;
  } catch (error) {
    console.log('Error while updateIntegration:', error);
    // throw error;
  }

  return undefined;
};

export const createGroupFunc = async (variables: GroupSettings) => {
  try {
    const result: any = await API.graphql({
      query: createGroup,
      variables,
      authMode: 'AWS_IAM',
    });

    return result.data.createGroup as GroupSettings;
  } catch (error) {
    console.log('Error while createGroup:', error);
    throw error;
  }

  // return undefined;
};

export const updateGroupSettingsFunc = async (variables: GroupSettings) => {
  try {
    const result: any = await API.graphql({
      query: updateGroupSettings,
      variables
    });

    return result.data.updateGroupSettings as GroupSettings;
  } catch (error) {
    console.log('Error while updateGroupSettings:', error);
    // throw error;
  }

  return undefined;
};

export const createUserFunc = async (user: User) => {
  try {
    const result: any = await API.graphql({
      query: addUser,
      variables: user,
      authMode: 'AWS_IAM',
    });

    return result.data.addUser as any;
  } catch (error) {
    console.log('Error while addUser:', error);
    throw error;
  }

  // return undefined;
};

export const updateUserFunc = async ({ email, firstName, lastName } : {
  email: string, firstName: string, lastName: string,
}) => {
  try {
    const result: any = await API.graphql({
      query: updateUser,
      variables: {
        email,
        firstName,
        lastName,
      },
    });

    return result.data.updateUser as any;
  } catch (error) {
    console.log('Error while updateUser:', error);
    throw error;
  }

  // return undefined;
};

export const deleteUserFunc = async (user: User) => {
  try {
    const result: any = await API.graphql({
      query: deleteUser,
      variables: {
        email: user.email,
        group: user.group,
        username: user.username,
      },
    });

    return result.data.deleteUser as any;
  } catch (error) {
    console.log('Error while deleteUser:', error);
    // throw error;
  }

  return undefined;
};

export const resendUserInviteFunc = async ({ email, username } : {
  email: string, username: string
}) => {
  try {
    const result: any = await API.graphql({
      query: resendUserInvite,
      variables: {
        email,
        username,
      },
    });

    return result.data.resendUserInvite as any;
  } catch (error) {
    console.log('Error while resendUserInvite:', error);
  }

  return undefined;
};

export const createWorkspaceFunc = async ({ name, email, website, isPublic, isShared, investmentThesis, tags, investmentStages  } : {
  name: string, email?: string, website?: string, isPublic?: boolean, isShared?: boolean, investmentThesis?: InvestmentThesis, tags?: string[]|null, investmentStages?: string[]|null,
}) => {
  try {
    const result: any = await API.graphql({
      query: createWorkspace,
      variables: {
        name,
        email,
        website,
        isPublic,
        isShared,
        investmentThesis,
        tags,
        investmentStages,
      },
    });

    return result.data.createWorkspace as any;
  } catch (error) {
    console.log('Error while createWorkspace:', error);
    // throw error;
  }

  return undefined;
};

export const updateWorkspaceFunc = async ({ id, name, email, website, isPublic, investmentThesis, tags, investmentStages  } : {
  id: string, name: string, email: string, website: string, isPublic: boolean, investmentThesis: InvestmentThesis, tags?: string[]|null, investmentStages?: string[]|null,
}) => {
  try {
    const result: any = await API.graphql({
      query: updateWorkspace,
      variables: {
        id,
        name,
        email,
        website,
        isPublic,
        investmentThesis,
        tags,
        investmentStages,
      },
    });

    return result.data.updateWorkspace as any;
  } catch (error) {
    console.log('Error while updateWorkspace:', error);
    // throw error;
  }

  return undefined;
};

export const deleteWorkspaceFunc = async ({ id } : {
  id: string
}) => {
  try {
    const result: any = await API.graphql({
      query: deleteWorkspace,
      variables: {
        id,
      },
    });

    return result.data.deleteWorkspace as any;
  } catch (error) {
    console.log('Error while deleteWorkspace:', error);
    // throw error;
  }

  return undefined;
};

export const createDashboardFunc = async (newDashboard: any) => {
  let mutationData: boolean | undefined = undefined;

  try {
    const variables = setNullOrder(structuredClone(newDashboard));
    if (!variables.refreshData)
      variables.refreshData = {};
    variables.refreshData.lastUpdatedBy = instanceHelper.getId();
    await API.graphql({
      query: createDashboard,
      variables,
    });
    mutationData = true;
  } catch (error) {
    console.log('Error while createDashboard:', error);
    // throw error;
    mutationData = false;
  }

  return mutationData;
};

export const updateDashboardFunc = async (newDashboard: any) => {
  let mutationData: boolean | undefined = undefined;

  try {
    const variables = setNullOrder(structuredClone(newDashboard));
    if (!variables.refreshData)
      variables.refreshData = {};
    variables.refreshData.lastUpdatedBy = instanceHelper.getId();
    await API.graphql({
      query: updateDashboard,
      variables,
    });
    mutationData = true;
  } catch (error) {
    console.log('Error while updateDashboard:', error);
    // throw error;
    mutationData = false;
  }

  return mutationData;
};

export const deleteDashboardFunc = async (id: string) => {
  try {
    const result: any = await API.graphql({
      query: deleteDashboard,
      variables: { id },
    });

    return result.data.deleteDashboard as any;
  } catch (error) {
    console.log('Error while deleteDashboard:', error);
    // throw error;
  }

  return undefined;
};

export const addDashboardQueryFunc = async (dashboardId: string, title: string, group: string, query: string, answer: string, history: string | undefined, order: number, manualOverride: boolean, source: DashboardQuerySource, deleted?: boolean) => {
  try {
    const result: any = await API.graphql({
      query: addDashboardQuery,
      variables: {
        dashboardId,
        title,
        group,
        query,
        answer,
        history,
        order,
        manualOverride,
        source,
        deletedAt: deleted ? new Date().toISOString() : undefined,
      },
    });

    return result.data.addDashboardQuery as any;
  } catch (error) {
    console.log('Error while addDashboardQuery:', error);
    // throw error;
  }

  return undefined;
}

export const refreshDashboardQueryFunc = async (dashboardId: string, title: string, group: string, query: string, order: number, source: DashboardQuerySource) => {
  try {
    const result: any = await API.graphql({
      query: refreshDashboardQuery,
      variables: {
        dashboardId,
        title,
        group,
        query,
        order,
        source,
      },
    });

    return result.data.refreshDashboardQuery as any;
  } catch (error) {
    console.log('Error while refreshDashboardQuery:', error);
    // throw error;
  }

  return undefined;
};

export const deleteDashboardQueryFunc = async (id: string, title: string, group: string) => {
  try {
    const result: any = await API.graphql({
      query: deleteDashboardQuery,
      variables: {
        id,
        title,
        group
      },
    });

    return result.data.deleteDashboardQuery as any;
  } catch (error) {
    console.log('Error while deleteDashboardQuery:', error);
    // throw error;
  }

  return undefined;
};

export const assignContentToDashboardFunc = async ({ unassignedContentId, dashboards } : {
  unassignedContentId: string, dashboards: AssignUnassignedContentInput[]
}) => {
  try {
    const result: any = await API.graphql({
      query: assignContentToDashboard,
      variables: {
        unassignedContentId,
        dashboards,
      },
    });

    return result.data.assignContentToDashboard as any;
  } catch (error) {
    console.log('Error while assignContentToDashboard:', error);
    // throw error;
  }

  return undefined;
};

export const addEmailBodyToDashboardFunc = async ({ id, emailBody } : {
  id: string, emailBody: EmailBody
}) => {
  try {
    const result: any = await API.graphql({
      query: addEmailBodyToDashboard,
      variables: {
        id,
        emailBody,
      },
    });

    return result.data.addEmailBodyToDashboard as any;
  } catch (error) {
    console.log('Error while addEmailBodyToDashboard:', error);
    // throw error;
  }

  return undefined;
};

export const addWorkspaceToDashboardFunc = async ({ dashboardId, workspaceId, group } : {
  dashboardId: string, workspaceId: string, group: string
}) => {
  try {
    const result: any = await API.graphql({
      query: addWorkspaceToDashboard,
      variables: {
        dashboardId,
        workspaceId,
        group,
      },
    });

    return result.data.addWorkspaceToDashboard as any;
  } catch (error) {
    console.log('Error while addWorkspaceToDashboard:', error);
    // throw error;
  }

  return undefined;
};

export const removeWorkspaceFromDashboardFunc = async ({ dashboardId, workspaceId, group } : {
  dashboardId: string, workspaceId: string, group: string
}) => {
  try {
    const result: any = await API.graphql({
      query: removeWorkspaceFromDashboard,
      variables: {
        dashboardId,
        workspaceId,
        group,
      },
    });

    return result.data.removeWorkspaceFromDashboard as any;
  } catch (error) {
    console.log('Error while removeWorkspaceFromDashboard:', error);
    // throw error;
  }

  return undefined;
};

export const refreshWorkspaceScoreFunc = async ({ dashboardId, workspaceId, group } : {
  dashboardId: string, workspaceId: string, group: string
}) => {
  try {
    const result: any = await API.graphql({
      query: refreshWorkspaceScore,
      variables: {
        dashboardId,
        workspaceId,
        group,
      },
    });

    return result.data.refreshWorkspaceScore as any;
  } catch (error) {
    console.log('Error while refreshWorkspaceScore:', error);
  }

  return undefined;
};

export const addWorkspaceUserMembershipFunc = async ({ workspaceId, email, name } : {
  workspaceId: string, email: string, name: string
}) => {
  try {
    const result: any = await API.graphql({
      query: addWorkspaceUserMembership,
      variables: {
        workspaceId,
        email,
        name,
      },
    });

    return result.data.addWorkspaceUserMembership as any;
  } catch (error) {
    console.log('Error while addWorkspaceUserMembership:', error);
    // throw error;
  }

  return undefined;
};

export const deleteWorkspaceUserMembershipFunc = async ({ workspaceId, email } : {
  workspaceId: string, email: string
}) => {
  try {
    const result: any = await API.graphql({
      query: deleteWorkspaceUserMembership,
      variables: {
        workspaceId,
        email,
      },
    });

    return result.data.deleteWorkspaceUserMembership as any;
  } catch (error) {
    console.log('Error while deleteWorkspaceUserMembership:', error);
  }

  return undefined;
};

export const updateCurrentUserWorkspaceUserMembershipFunc = async ({ workspaceId, status } : {
  workspaceId: string, status: string
}) => {
  try {
    const result: any = await API.graphql({
      query: updateCurrentUserWorkspaceUserMembership,
      variables: {
        workspaceId,
        status,
      },
    });

    return result.data.updateCurrentUserWorkspaceUserMembership as any;
  } catch (error) {
    console.log('Error while updateCurrentUserWorkspaceUserMembership:', error);
    // throw error;
  }

  return undefined;
};

export const sendShareWorkspaceEmailFunc = async ({ workspaceId, toAddress, replyToAddress, dashboardIds } : {
  workspaceId: string, toAddress: string, replyToAddress: string, dashboardIds: string[]
}) => {
  try {
    const result: any = await API.graphql({
      query: sendShareWorkspaceEmail,
      variables: {
        workspaceId,
        toAddress,
        replyToAddress,
        dashboardIds
      },
    });

    return result.data.sendShareWorkspaceEmail as any;
  } catch (error) {
    console.log('Error while sendShareWorkspaceEmail:', error);
    // throw error;
  }

  return undefined;
};

export const sendRequestIntroEmailFunc = async ({ workspaceId, dashboardId, requestorEmail } : {
  workspaceId: string, dashboardId: string, requestorEmail: string
}) => {
  try {
    const result: any = await API.graphql({
      query: sendRequestIntroEmail,
      variables: {
        dashboardId,
        workspaceId,
        requestorEmail,
      },
    });

    return result.data.sendRequestIntroEmail as any;
  } catch (error) {
    console.log('Error while sendRequestIntroEmail:', error);
    // throw error;
  }

  return undefined;
};

export const updateInvestmentThesisFunc = async ({ id, group, investmentThesis } : {
  id: string, group: string, investmentThesis: InvestmentThesis
}) => {
  try {
    const result: any = await API.graphql({
      query: updateInvestmentThesis,
      variables: {
        id,
        group,
        investmentThesis,
      },
      authMode: "AWS_IAM",
    });

    return result.data.updateInvestmentThesis as any;
  } catch (error) {
    console.log('Error while updateInvestmentThesis:', error);
    // throw error;
  }

  return undefined;
};

export const createUnassignedContentFunc = async (variables: UnassignedContent) => {
  try {
    const result: any = await API.graphql({
      query: createUnassignedContent,
      variables
    });

    return result.data.createUnassignedContent as any;
  } catch (error) {
    console.log('Error while createUnassignedContent:', error);
    // throw error;
  }

  return undefined;
};

export const deleteUnassignedContentFunc = async ({ id, group } : {
  id: string, group: string
}) => {
  try {
    const result: any = await API.graphql({
      query: deleteUnassignedContent,
      variables: {
        id,
        group,
      },
    });

    return result.data.deleteUnassignedContent as any;
  } catch (error) {
    console.log('Error while deleteUnassignedContent:', error);
    // throw error;
  }

  return undefined;
};

export const createDocSendIngestionFunc = async (variables: DocSendIngestion) => {
  let mutationData: boolean | undefined = undefined;

  try {
    await API.graphql({
      query: createDocSendIngestion,
      variables
    });
    mutationData = true;
  } catch (error) {
    console.log('Error while createDocSendIngestion:', error);
    // throw error;
    mutationData =  false;
  }

  return mutationData;
};

export const runFileSyncFunc = async (group: string) => {
  try {
    const result: any = await API.graphql({
      query: runFileSync,
      variables: {
        group,
      },
    });

    return result.data.runFileSync as any;
  } catch (error) {
    console.log('Error while runFileSync:', error);
    // throw error;
  }

  return undefined;
};

export const updateFileFunc = async ({ id, group, documentCategory, status, dashboardId, marketMetrics, isDeck } : {
  id: string, group: string, documentCategory?: string, status: string, dashboardId?: string, marketMetrics?: MarketMetric[], isDeck?: boolean
}) => {
  try {
    const result: any = await API.graphql({
      query: updateFile,
      variables: {
        id,
        group,
        documentCategory,
        status,
        dashboardId,
        marketMetrics,
        isDeck,
      },
    });

    return result.data.updateFile as any;
  } catch (error) {
    console.log('Error while updateFile:', error);
    // throw error;
  }

  return undefined;
};
